// @ts-nocheck
import { forwardRef, useCallback, useState, useRef, useEffect } from 'react';
import { Card, Box, IconButton, Typography, useTheme } from '@mui/material';
import capitalize from 'lodash/capitalize';
import { ReactComponent as CloseIcon } from '../../../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as SuccessIcon } from '../../../../../assets/Icons/lucideIcons/circle-check.svg';
import { ReactComponent as PendingIcon } from '../../../../../assets/Icons/lucideIcons/refresh-cw.svg';
import { ReactComponent as PartialIcon } from '../../../../../assets/Icons/lucideIcons/hourglass.svg';
import PropTypes from 'prop-types';
import { SnackbarContent, useSnackbar } from 'notistack';

import AutomationResultModal from './AutomationResultModal';

const SSE_TIMER = 10000; // 10 sec

const genStatus = (status, theme) => {
  const baseStyle = {
    color: 'white',
    fontSize: '20px',
    borderRadius: '50%',
    padding: '1px',
  };
  const statusMessage = status?.toLowerCase();
  if (statusMessage === 'success') {
    return (
      <SuccessIcon
        sx={{
          ...baseStyle,
          backgroundColor: `${theme?.palette?.sseNotification?.successIconColor}`,
        }}
      />
    );
  } else if (statusMessage === 'partial') {
    return (
      <PartialIcon
        width={20}
        height={20}
        style={{
          ...baseStyle,
          backgroundColor: theme?.palette?.sseNotification?.partialIconColor,
        }}
      />
    );
  } else if (statusMessage === 'failed') {
    return (
      <CloseIcon
        width={20}
        height={20}
        style={{
          ...baseStyle,
          backgroundColor: theme?.palette?.sseNotification?.failedIconColor,
        }}
      />
    );
  } else {
    return (
      <PendingIcon
        width={20}
        height={20}
        style={{
          ...baseStyle,
          backgroundColor: theme?.palette?.sseNotification?.pendingIconColor,
        }}
      />
    );
  }
};

const AutomationReportToast = forwardRef(
  function AutomationReportToast(props, ref) {
    const { id, message } = props;
    const [open, setOpen] = useState(false);
    const isModalOpenRef = useRef(false);
    const { closeSnackbar } = useSnackbar();
    const theme = useTheme();

    const data = JSON.parse(message);

    const handleDismiss = useCallback(() => {
      setOpen(false);
      isModalOpenRef.current = false;
      closeSnackbar(id);
    }, [setOpen, closeSnackbar, id]);

    const handleViewDetails = useCallback(() => {
      setOpen(true);
      isModalOpenRef.current = true;
    }, [setOpen]);

    useEffect(() => {
      const timeout = setTimeout(() => {
        if (!isModalOpenRef.current) {
          closeSnackbar(id);
        }
      }, SSE_TIMER);

      return () => clearTimeout(timeout);
    }, [closeSnackbar, id]);

    return (
      <>
        <SnackbarContent ref={ref}>
          <Card
            sx={{
              boxShadow: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'start',
              width: '300px',
              padding: '15px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: '15px',
              }}
            >
              {genStatus(data?.event_status, theme)}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                }}
              >
                <Typography variant="body6">
                  {capitalize(data?.event_type)}: {data?.execution_status}
                </Typography>
                <Box
                  className="infoTextStyle"
                  component="button"
                  onClick={handleViewDetails}
                  sx={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    font: 'inherit',
                    textAlign: 'left',
                  }}
                >
                  view details
                </Box>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleDismiss} sx={{ mt: -1 }}>
              <CloseIcon width={24} height={24} />
            </IconButton>
          </Card>
        </SnackbarContent>
        {open && (
          <AutomationResultModal
            handleClose={handleDismiss}
            open={open}
            taskResult={{ result: data?.events_data?.result_data }}
          />
        )}
      </>
    );
  }
);

AutomationReportToast.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  message: PropTypes.string.isRequired,
};

export default AutomationReportToast;
